import { api, type MarketSymbol } from "../api";
import { useApiData } from "../useApi";
import { PageState, formatTime } from "../components";

export function Market({ onSignedOut }: { onSignedOut: () => void }) {
  const { data, error, loading, reload } = useApiData(() => api.market(), onSignedOut);

  return (
    <>
      <h1>Market Monitor</h1>
      <p className="subtitle">
        Closed {data?.timeframe ?? "M5"} bars and the latest quote, as the terminal
        reported them.{" "}
        <button className="link" onClick={reload}>
          Refresh
        </button>
      </p>

      <PageState
        loading={loading}
        error={error}
        empty={data?.symbols.length === 0}
        emptyMessage="No market data yet — the EA has not pushed any quotes or bars."
      >
        {data?.symbols.map((symbol) => (
          <SymbolCard key={symbol.provider_symbol} symbol={symbol} />
        ))}
      </PageState>
    </>
  );
}

function SymbolCard({ symbol }: { symbol: MarketSymbol }) {
  // A price that stopped updating looks identical to a live one, so the age
  // is what actually tells an operator whether this is worth trusting.
  const age = symbol.quote_age_seconds;
  const stale = age !== null && age > 120;

  return (
    <div className="card">
      <h2>{symbol.provider_symbol}</h2>

      <div className="metrics">
        <div className="metric">
          <div className="label">Bid</div>
          <div className="value">{symbol.bid ?? "—"}</div>
        </div>
        <div className="metric">
          <div className="label">Ask</div>
          <div className="value">{symbol.ask ?? "—"}</div>
        </div>
        <div className="metric">
          <div className="label">Spread (points)</div>
          <div className="value">{symbol.spread_points ?? "—"}</div>
        </div>
        <div className="metric">
          <div className="label">Price age</div>
          <div className="value">
            {symbol.bid === null ? (
              <span className="badge warn">no quote yet</span>
            ) : age === null ? (
              // The EA sent a price but not its time, so the age is genuinely
              // unknown. Borrowing quote_sent_at here would read "2s" beside a
              // price frozen since Friday.
              <span className="badge warn" title="EA build does not report SYMBOL_TIME">
                unknown
              </span>
            ) : (
              <span className={`badge ${stale ? "warn" : "ok"}`}>{age}s</span>
            )}
          </div>
        </div>
        <div className="metric">
          <div className="label">Bars held</div>
          <div className="value">{symbol.candle_count}</div>
        </div>
        <div className="metric">
          <div className="label">Gaps</div>
          <div className="value">
            <span className={`badge ${symbol.gap_count > 0 ? "warn" : "ok"}`}>
              {symbol.gap_count}
            </span>
          </div>
        </div>
      </div>

      {symbol.gap_count > 0 && (
        <div className="notice" style={{ marginTop: 14, marginBottom: 0 }}>
          {symbol.gap_count} gap(s) in the stored series. Missing bars are reported, never
          filled in — a weekend or a terminal that was switched off both show up here.
        </div>
      )}

      {symbol.candles.length > 0 && (
        <>
          <Sparkline candles={symbol.candles} />
          <p className="dim" style={{ margin: "6px 0 0" }}>
            Last bar opened {formatTime(symbol.last_candle_open_time)}
          </p>
        </>
      )}
    </div>
  );
}

/** A close-price line. Deliberately not a candlestick chart: at a glance the
 * question is "is this moving and is it sane", and a full OHLC renderer is a
 * dependency this page does not need to answer that. */
function Sparkline({ candles }: { candles: MarketSymbol["candles"] }) {
  const closes = candles.map((c) => Number(c.close)).filter((n) => Number.isFinite(n));
  if (closes.length < 2) return null;

  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = max - min || 1;
  const width = 600;
  const height = 90;

  const points = closes
    .map((close, index) => {
      const x = (index / (closes.length - 1)) * width;
      const y = height - ((close - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const rising = (closes.at(-1) ?? 0) >= (closes[0] ?? 0);

  return (
    <div style={{ marginTop: 16 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 90, display: "block" }}
        role="img"
        aria-label={`${closes.length} closing prices, ${min} to ${max}`}
      >
        <polyline
          points={points}
          fill="none"
          stroke={rising ? "var(--ok)" : "var(--danger)"}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="dim mono" style={{ display: "flex", justifyContent: "space-between" }}>
        <span>{min}</span>
        <span>{closes.length} bars</span>
        <span>{max}</span>
      </div>
    </div>
  );
}
